import React, { useMemo, useState } from "react";
import options from "components/PLCTABS/SFLdata/addressOptions";

function maskEmail(email) {
  const [user, domain] = email.split("@");
  if (!user || !domain) return email;
  if (user.length <= 2) return `${user[0]}*@${domain}`;
  return `${user.slice(0, 2)}***@${domain}`;
}

function formatPhoneE164ish(phone) {
  // Assumes +254733774000 -> +254 733 774 000 for display
  const p = phone.replace(/\s+/g, "");
  if (!p.startsWith("+254")) return phone;
  const rest = p.slice(4);
  // basic grouping: 3-3-3
  const a = rest.slice(0, 3);
  const b = rest.slice(3, 6);
  const c = rest.slice(6, 9);
  return `+254 ${a}${b ? " " + b : ""}${c ? " " + c : ""}`;
}

function titleCase(s) {
  return s
    .trim()
    .split(/\s+/)
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase())
    .join(" ");
}

export default function PaymentConfirmationModal({
  authorization,
  customer,
  amountLabel,
  merchantName,
  selectedCurrencyType,
}) {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [copied, setCopied] = useState(false);

  const cardBrand = useMemo(() => {
    const brand = authorization.brand || authorization.card_type || "card";
    return titleCase(brand);
  }, [authorization.brand, authorization.card_type]);

  const countryLabel = useMemo(() => {
    return options.countries.find(
      (country) => country.value === authorization.country_code?.toUpperCase()
    )?.label;
  }, [authorization.country_code]);

  const expLabel = `${authorization.exp_month} / ${authorization.exp_year}`;

  const displayPhone = formatPhoneE164ish(
    customer.international_format_phone || customer.phone
  );

  const displayName = useMemo(() => {
    const fn = customer.first_name?.trim();
    const ln = customer.last_name?.trim();
    const full = [fn, ln].filter(Boolean).join(" ");
    return full || null;
  }, [customer.first_name, customer.last_name]);

  async function handleCopy(code) {
    try {
      await navigator.clipboard.writeText(code);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 1200);
    } catch {
      // no-op; you can add a fallback if needed
    }
  }

  return (
    <div className="w-full bg-yellow-50 rounded">
      {/* Modal */}
      <div className="relative w-full max-w-lg overflow-hidden rounded-2xl shadow-2xl">
        {/* Header */}
        <div className="flex items-start gap-3 border-b border-slate-200 px-5 py-4">
          <div className="grid h-10 w-10 place-items-center rounded-xl bg-sky-500/10 text-sky-700">
            <span aria-hidden>🔒</span>
          </div>

          <div className="min-w-0">
            <h2
              id="pc-title"
              className="text-base font-semibold tracking-tight text-slate-900"
            >
              Confirm your payment
            </h2>
            <p className="mt-1 text-sm leading-snug text-slate-500">
              You’re about to complete a secure card payment. Review the details
              before confirming.
            </p>

            {(merchantName || amountLabel) && (
              <div className="mt-3 flex flex-wrap items-center gap-2">
                {merchantName && (
                  <span className="inline-flex items-center rounded-full border border-slate-200 bg-white px-3 py-1 text-xs font-medium text-slate-700">
                    {merchantName}
                  </span>
                )}
                {amountLabel && (
                  <span className="inline-flex items-center rounded-full border border-sky-200 bg-sky-50 px-3 py-1 text-xs font-semibold text-sky-800">
                    {`${selectedCurrencyType} ${amountLabel}`}
                  </span>
                )}
              </div>
            )}
          </div>
        </div>

        {/* Body */}
        <div className="space-y-3 px-5 py-4">
          {/* Payment Method */}
          <section className="rounded-2xl border border-slate-200 bg-white p-4">
            <div className="mb-3 flex items-center gap-2">
              <span aria-hidden>💳</span>
              <h3 className="text-sm font-semibold text-slate-900">
                Payment method
              </h3>

              <span
                className={[
                  "ml-auto inline-flex items-center rounded-full px-2.5 py-1 text-xs font-medium",
                  authorization.reusable
                    ? "border border-emerald-200 bg-emerald-50 text-emerald-700"
                    : "border border-slate-200 bg-slate-50 text-slate-600",
                ].join(" ")}
              >
                {authorization.reusable ? "Reusable" : "One-time"}
              </span>
            </div>

            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2 font-semibold text-slate-900">
                <span className="h-2.5 w-2.5 rounded-full bg-sky-500" />
                <span>{cardBrand}</span>
              </div>
              <div className="text-sm text-slate-500">{countryLabel}</div>
            </div>

            <div className="mt-3 divide-y divide-dashed divide-slate-200">
              <Row
                label="Card"
                value={`•••• •••• •••• ${authorization.last4}`}
              />
              <Row label="Expires" value={expLabel} />
              <Row label="Bank" value={authorization.bank} />
            </div>
          </section>

          {/* Customer */}
          <section className="rounded-2xl border border-slate-200 bg-white p-4">
            <div className="mb-3 flex items-center gap-2">
              <span aria-hidden>👤</span>
              <h3 className="text-sm font-semibold text-slate-900">
                Customer details
              </h3>
            </div>

            <div className="divide-y divide-dashed divide-slate-200">
              <Row label="Email" value={customer.email} mono />
              <Row label="Phone" value={displayPhone} />
              {displayName && <Row label="Name" value={displayName} />}
            </div>
          </section>

          {/* Authorization */}
          {/* <section className="rounded-2xl border border-slate-200 bg-white p-4">
                        <div className="mb-3 flex items-center gap-2">
                            <span aria-hidden>🔎</span>
                            <h3 className="text-sm font-semibold text-slate-900">
                                Authorization
                            </h3>
                        </div>

                        <div className="divide-y divide-dashed divide-slate-200">
                            <Row label="Channel" value={titleCase(authorization.channel)} />
                            <Row label="Card type" value={titleCase(authorization.card_type)} />

                            <div className="flex items-center justify-between py-2.5">
                                <div className="text-sm text-slate-500">Authorization code</div>
                                <div className="flex items-center gap-2">
                                    <span className="rounded-xl border border-slate-200 bg-slate-50 px-3 py-1.5 font-mono text-xs text-slate-800">
                                        {authorization.authorization_code}
                                    </span>
                                    <button
                                        type="button"
                                        onClick={() => handleCopy(authorization.authorization_code)}
                                        className="rounded-xl border border-slate-200 bg-white px-3 py-2 text-xs font-semibold text-slate-700 hover:bg-slate-50"
                                    >
                                        {copied ? "Copied" : "Copy"}
                                    </button>
                                </div>
                            </div>
                        </div>
                    </section> */}

          {/* Security Notice */}
          {/* <div className="flex gap-3 rounded-2xl border border-sky-200 bg-sky-50 px-4 py-3">
                        <div className="grid h-9 w-9 place-items-center rounded-xl border border-sky-200 bg-white text-sky-700">
                            <span aria-hidden>🔐</span>
                        </div>
                        <div className="min-w-0">
                            <div className="text-sm font-semibold text-slate-900">
                                Security notice
                            </div>
                            <div className="mt-0.5 text-sm leading-snug text-slate-600">
                                This transaction is protected with industry-standard encryption.
                                Your card details are never shared with the merchant.
                            </div>
                        </div>
                    </div> */}

          <p className="px-1 text-xs leading-snug text-slate-500">
            By clicking <span className="font-semibold">Confirm</span>, you
            authorize this card for the current transaction.
          </p>
        </div>
      </div>
    </div>
  );
}

function Row({ label, value, mono, valueHint }) {
  return (
    <div className="flex items-center justify-between gap-4 py-2.5">
      <div className="text-sm text-slate-500">{label}</div>
      <div className="text-right text-sm text-slate-900">
        <span className={mono ? "font-mono text-[13px]" : ""}>{value}</span>
        {valueHint && (
          <span className="ml-2 text-xs text-slate-500">{valueHint}</span>
        )}
      </div>
    </div>
  );
}
